import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      brand: "SubGen",
      tagline: "AI-Powered Subtitle Generation",
      nav: {
        features: "Features",
        pricing: "Pricing",
        about: "About",
        support: "Support"
      },
      upload: {
        title: "Upload Your Video or Audio",
        description: "Supported formats: MP4, AVI, MOV, MP3, WAV, M4A (Max 2GB)",
        dragDrop: "Drag and drop your file here, or",
        browse: "browse",
        urlPlaceholder: "https://youtube.com/watch?v=...",
        load: "Load",
        jobInProgress: "You already have a video processing job in progress. Please wait for it to complete before uploading another video.",
        jobInProgressDetails: "Job in progress: {type} ({status})"
      },
      processing: {
        title: "Processing Status",
        fileAnalysis: "File Analysis Complete",
        audioExtraction: "Audio Extraction",
        speechRecognition: "Speech Recognition (Preview)",
        translation: "Translation (Optional)",
        complete: "✓ Complete",
        processing: "Processing...",
        pending: "Pending",
        sourceLanguage: "Source Language",
        targetLanguage: "Translate To",
        autoDetect: "Auto-detect",
        noTranslation: "No translation"
      },
      preview: {
        title: "Preview (First {duration} seconds)",
        generatedSubtitles: "Generated Subtitles",
        edit: "Edit",
        download: "Download SRT",
        subtitleFormat: "Subtitle Format"
      },
      payment: {
        title: "Get Full Video Subtitles",
        description: "Process the complete video and download high-quality subtitles",
        features: {
          fullVideo: "Full video processing",
          highQuality: "High-quality subtitles",
          multipleFormats: "Multiple formats",
          translationIncluded: "Translation included"
        },
        paypal: "PayPal",
        paypalDesc: "Pay with PayPal account",
        creditCard: "Credit Card",
        via2Checkout: "Via 2Checkout",
        security: {
          secure: "Secure Payment",
          ssl: "SSL encrypted",
          instant: "Instant Download",
          afterProcessing: "After processing",
          autoDelete: "Auto-Delete",
          filesRemoved: "Files removed after 1 hour"
        }
      },
      queue: {
        title: "Full Video Processing",
        queuePosition: "Queue Position: #{position}",
        estimatedWait: "Estimated wait time: {time}",
        batchProcessing: "Batch Processing ({current}/{total} segments)",
        currentSegment: "Current Segment: {timeRange}",
        stayOnPage: "Stay on this page",
        notificationDesc: "You'll be notified when processing is complete and files are ready for download."
      },
      download: {
        title: "Processing Complete!",
        description: "Your subtitles are ready for download",
        subtitleFiles: "Subtitle Files",
        videoWithSubtitles: "Video with Subtitles",
        downloadAll: "Download All Files",
        autoDeleteWarning: "Files will be automatically deleted in {time} minutes"
      },
      languages: {
        english: "English",
        german: "German",
        french: "French",
        spanish: "Spanish",
        italian: "Italian"
      },
      footer: {
        features: "Features",
        speechRecognition: "Speech Recognition",
        multiLanguage: "Multi-language Support",
        autoTranslation: "Automatic Translation",
        multipleFormats: "Multiple Formats",
        support: "Support",
        helpCenter: "Help Center",
        apiDocs: "API Documentation",
        contact: "Contact Us",
        status: "Status Page",
        legal: "Legal",
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        cookies: "Cookie Policy",
        copyright: "© 2024 SubGen. All rights reserved."
      },
      home: {
        hero: {
          title: "Video to Subtitle Generator",
          subtitle: "Create SRT Subtitles from Video",
          description: "Convert video to subtitle format with AI-powered transcription. Generate accurate SRT files, support multiple languages, and preview for free. The best online video subtitle generator."
        },
        features: {
          title: "Best Video to Subtitle Converter Features"
        },
        pricing: {
          title: "Simple, Transparent Pricing",
          price: "$0.045",
          perMinute: "per minute",
          description: "No subscriptions. No hidden fees. Pay only for what you process.",
          freePreview: "Free Preview (20-30 seconds)",
          multipleFormats: "Multiple Formats (SRT, VTT, ASS)",
          autoTranslation: "Auto Translation"
        },
        testimonials: {
          title: "Trusted by Content Creators Worldwide"
        },
        technology: {
          title: "Advanced AI Video to Subtitle Technology",
          description: "Our video subtitle generator uses OpenAI's Whisper AI for accurate transcription and Google Translate for multi-language subtitle generation. The most advanced video to SRT converter available."
        }
      }
    }
  },
  de: {
    translation: {
      brand: "SubGen",
      tagline: "KI-gestützte Untertitel-Generierung",
      nav: {
        features: "Funktionen",
        pricing: "Preise",
        about: "Über uns",
        support: "Support"
      },
      upload: {
        title: "Laden Sie Ihr Video oder Audio hoch",
        description: "Unterstützte Formate: MP4, AVI, MOV, MP3, WAV, M4A (Max 2GB)",
        dragDrop: "Datei hier ablegen oder",
        browse: "durchsuchen",
        urlPlaceholder: "https://youtube.com/watch?v=...",
        load: "Laden",
        jobInProgress: "Sie haben bereits einen Videoverarbeitungsauftrag in Bearbeitung. Bitte warten Sie, bis er abgeschlossen ist, bevor Sie ein anderes Video hochladen.",
        jobInProgressDetails: "Auftrag in Bearbeitung: {type} ({status})"
      },
      processing: {
        title: "Verarbeitungsstatus",
        fileAnalysis: "Dateianalyse abgeschlossen",
        audioExtraction: "Audio-Extraktion",
        speechRecognition: "Spracherkennung (Vorschau)",
        translation: "Übersetzung (Optional)",
        complete: "✓ Abgeschlossen",
        processing: "Verarbeitung...",
        pending: "Ausstehend",
        sourceLanguage: "Quellsprache",
        targetLanguage: "Übersetzen nach",
        autoDetect: "Automatisch erkennen",
        noTranslation: "Keine Übersetzung"
      },
      preview: {
        title: "Vorschau (Erste {duration} Sekunden)",
        generatedSubtitles: "Generierte Untertitel",
        edit: "Bearbeiten",
        download: "SRT herunterladen",
        subtitleFormat: "Untertitel-Format"
      },
      payment: {
        title: "Vollständige Video-Untertitel erhalten",
        description: "Das komplette Video verarbeiten und hochwertige Untertitel herunterladen",
        features: {
          fullVideo: "Vollständige Videoverarbeitung",
          highQuality: "Hochwertige Untertitel",
          multipleFormats: "Mehrere Formate",
          translationIncluded: "Übersetzung inbegriffen"
        },
        paypal: "PayPal",
        paypalDesc: "Mit PayPal-Konto bezahlen",
        creditCard: "Kreditkarte",
        via2Checkout: "Über 2Checkout",
        security: {
          secure: "Sichere Zahlung",
          ssl: "SSL-verschlüsselt",
          instant: "Sofortiger Download",
          afterProcessing: "Nach der Verarbeitung",
          autoDelete: "Auto-Löschen",
          filesRemoved: "Dateien nach 1 Stunde entfernt"
        }
      },
      queue: {
        title: "Vollständige Videoverarbeitung",
        queuePosition: "Warteschlangenposition: #{position}",
        estimatedWait: "Geschätzte Wartezeit: {time}",
        batchProcessing: "Batch-Verarbeitung ({current}/{total} Segmente)",
        currentSegment: "Aktuelles Segment: {timeRange}",
        stayOnPage: "Auf dieser Seite bleiben",
        notificationDesc: "Sie werden benachrichtigt, wenn die Verarbeitung abgeschlossen ist und die Dateien zum Download bereit sind."
      },
      download: {
        title: "Verarbeitung abgeschlossen!",
        description: "Ihre Untertitel sind zum Download bereit",
        subtitleFiles: "Untertitel-Dateien",
        videoWithSubtitles: "Video mit Untertiteln",
        downloadAll: "Alle Dateien herunterladen",
        autoDeleteWarning: "Dateien werden automatisch in {time} Minuten gelöscht"
      },
      languages: {
        english: "Englisch",
        german: "Deutsch",
        french: "Französisch",
        spanish: "Spanisch",
        italian: "Italienisch"
      },
      footer: {
        features: "Funktionen",
        speechRecognition: "Spracherkennung",
        multiLanguage: "Mehrsprachige Unterstützung",
        autoTranslation: "Automatische Übersetzung",
        multipleFormats: "Mehrere Formate",
        support: "Support",
        helpCenter: "Hilfezentrum",
        apiDocs: "API-Dokumentation",
        contact: "Kontakt",
        status: "Status-Seite",
        legal: "Rechtliches",
        privacy: "Datenschutz",
        terms: "Nutzungsbedingungen",
        cookies: "Cookie-Richtlinie",
        copyright: "© 2024 SubGen. Alle Rechte vorbehalten."
      },
      home: {
        hero: {
          title: "Video zu Untertitel Generator",
          subtitle: "SRT Untertitel aus Video erstellen",
          description: "Konvertieren Sie Video in Untertitel-Format mit KI-gestützter Transkription. Generieren Sie genaue SRT-Dateien, unterstützen Sie mehrere Sprachen und erhalten Sie eine kostenlose Vorschau. Der beste Online-Video-Untertitel-Generator."
        },
        features: {
          title: "Beste Video zu Untertitel Konverter Funktionen"
        },
        pricing: {
          title: "Einfache, transparente Preisgestaltung",
          price: "$0,10",
          perMinute: "pro Minute",
          description: "Keine Abonnements. Keine versteckten Gebühren. Zahlen Sie nur für das, was Sie verarbeiten.",
          freePreview: "Kostenlose Vorschau (20-30 Sekunden)",
          multipleFormats: "Mehrere Formate (SRT, VTT, ASS)",
          autoTranslation: "Automatische Übersetzung"
        },
        testimonials: {
          title: "Vertraut von Content Creators weltweit"
        },
        technology: {
          title: "Fortschrittliche KI Video zu Untertitel Technologie",
          description: "Unser Video-Untertitel-Generator verwendet OpenAI's Whisper AI für genaue Transkription und Google Translate für mehrsprachige Untertitel-Generierung. Der fortschrittlichste Video zu SRT Konverter verfügbar."
        }
      }
    }
  },
  fr: {
    translation: {
      brand: "SubGen",
      tagline: "Génération de sous-titres par IA",
      nav: {
        features: "Fonctionnalités",
        pricing: "Tarifs",
        about: "À propos",
        support: "Support"
      },
      upload: {
        title: "Téléchargez votre vidéo ou audio",
        description: "Formats supportés: MP4, AVI, MOV, MP3, WAV, M4A (Max 2GB)",
        dragDrop: "Glissez et déposez votre fichier ici, ou",
        browse: "parcourir",
        urlPlaceholder: "https://youtube.com/watch?v=...",
        load: "Charger",
        jobInProgress: "Vous avez déjà un travail de traitement vidéo en cours. Veuillez attendre qu'il se termine avant de télécharger une autre vidéo.",
        jobInProgressDetails: "Travail en cours: {type} ({status})"
      },
      processing: {
        title: "Statut du traitement",
        fileAnalysis: "Analyse du fichier terminée",
        audioExtraction: "Extraction audio",
        speechRecognition: "Reconnaissance vocale (Aperçu)",
        translation: "Traduction (Optionnel)",
        complete: "✓ Terminé",
        processing: "Traitement...",
        pending: "En attente",
        sourceLanguage: "Langue source",
        targetLanguage: "Traduire vers",
        autoDetect: "Détection automatique",
        noTranslation: "Pas de traduction"
      },
      preview: {
        title: "Aperçu (Premières {duration} secondes)",
        generatedSubtitles: "Sous-titres générés",
        edit: "Éditer",
        download: "Télécharger SRT",
        subtitleFormat: "Format des sous-titres"
      },
      payment: {
        title: "Obtenir les sous-titres de la vidéo complète",
        description: "Traiter la vidéo complète et télécharger des sous-titres de haute qualité",
        features: {
          fullVideo: "Traitement vidéo complet",
          highQuality: "Sous-titres haute qualité",
          multipleFormats: "Formats multiples",
          translationIncluded: "Traduction incluse"
        },
        paypal: "PayPal",
        paypalDesc: "Payer avec un compte PayPal",
        creditCard: "Carte de crédit",
        via2Checkout: "Via 2Checkout",
        security: {
          secure: "Paiement sécurisé",
          ssl: "Crypté SSL",
          instant: "Téléchargement instantané",
          afterProcessing: "Après traitement",
          autoDelete: "Suppression automatique",
          filesRemoved: "Dateis supprimés après 1 heure"
        }
      },
      queue: {
        title: "Traitement vidéo complet",
        queuePosition: "Position dans la file: #{position}",
        estimatedWait: "Temps d'attente estimé: {time}",
        batchProcessing: "Traitement par lot ({current}/{total} segments)",
        currentSegment: "Segment actuel: {timeRange}",
        stayOnPage: "Restez sur cette page",
        notificationDesc: "Vous serez notifié lorsque le traitement sera terminé et que les fichiers seront prêts à télécharger."
      },
      download: {
        title: "Traitement terminé!",
        description: "Vos sous-titres sont prêts à télécharger",
        subtitleFiles: "Fichiers de sous-titres",
        videoWithSubtitles: "Vidéo avec sous-titres",
        downloadAll: "Télécharger tous les fichiers",
        autoDeleteWarning: "Les fichiers seront automatiquement supprimés dans {time} minutes"
      },
      languages: {
        english: "Anglais",
        german: "Allemand",
        french: "Français",
        spanish: "Espagnol",
        italian: "Italien"
      },
      footer: {
        features: "Fonctionnalités",
        speechRecognition: "Reconnaissance vocale",
        multiLanguage: "Support multi-langues",
        autoTranslation: "Traduction automatique",
        multipleFormats: "Formats multiples",
        support: "Support",
        helpCenter: "Centre d'aide",
        apiDocs: "Documentation API",
        contact: "Nous contacter",
        status: "Page de statut",
        legal: "Légal",
        privacy: "Politique de confidentialité",
        terms: "Conditions d'utilisation",
        cookies: "Politique des cookies",
        copyright: "© 2024 SubGen. Tous droits réservés."
      },
      home: {
        hero: {
          title: "Générateur de sous-titres vidéo",
          subtitle: "Créer des sous-titres SRT à partir de vidéo",
          description: "Convertissez la vidéo au format sous-titre avec la transcription alimentée par l'IA. Générez des fichiers SRT précis, prenez en charge plusieurs langues et prévisualisez gratuitement. Le meilleur générateur de sous-titres vidéo en ligne."
        },
        features: {
          title: "Meilleures fonctionnalités de convertisseur vidéo vers sous-titres"
        },
        pricing: {
          title: "Tarification simple et transparente",
          price: "0,10 $",
          perMinute: "par minute",
          description: "Aucun abonnement. Aucuns frais cachés. Payez seulement pour ce que vous traitez.",
          freePreview: "Aperçu gratuit (20-30 secondes)",
          multipleFormats: "Formats multiples (SRT, VTT, ASS)",
          autoTranslation: "Traduction automatique"
        },
        testimonials: {
          title: "Approuvé par les créateurs de contenu du monde entier"
        },
        technology: {
          title: "Technologie avancée de conversion vidéo vers sous-titres par IA",
          description: "Notre générateur de sous-titres vidéo utilise l'IA Whisper d'OpenAI pour une transcription précise et Google Translate pour la génération de sous-titres multilingues. Le convertisseur vidéo vers SRT le plus avancé disponible."
        }
      }
    }
  },
  vi: {
    translation: {
      brand: "SubGen",
      tagline: "Tạo phụ đề bằng AI",
      nav: {
        features: "Tính năng",
        pricing: "Giá cả",
        about: "Về chúng tôi",
        support: "Hỗ trợ"
      },
      upload: {
        title: "Tải lên video hoặc audio của bạn",
        description: "Định dạng hỗ trợ: MP4, AVI, MOV, MP3, WAV, M4A (Tối đa 2GB)",
        dragDrop: "Kéo và thả file vào đây, hoặc",
        browse: "duyệt",
        urlPlaceholder: "https://youtube.com/watch?v=...",
        load: "Tải",
        jobInProgress: "Bạn đã có một công việc xử lý video đang tiến hành. Vui lòng đợi nó hoàn thành trước khi tải lên video khác.",
        jobInProgressDetails: "Công việc đang tiến hành: {type} ({status})"
      },
      processing: {
        title: "Trạng thái xử lý",
        fileAnalysis: "Phân tích file hoàn tất",
        audioExtraction: "Trích xuất âm thanh",
        speechRecognition: "Nhận dạng giọng nói (Xem trước)",
        translation: "Dịch thuật (Tùy chọn)",
        complete: "✓ Hoàn tất",
        processing: "Đang xử lý...",
        pending: "Chờ xử lý",
        sourceLanguage: "Ngôn ngữ nguồn",
        targetLanguage: "Dịch sang",
        autoDetect: "Tự động phát hiện",
        noTranslation: "Không dịch"
      },
      preview: {
        title: "Xem trước ({duration} giây đầu tiên)",
        generatedSubtitles: "Phụ đề đã tạo",
        edit: "Chỉnh sửa",
        download: "Tải xuống SRT",
        subtitleFormat: "Định dạng phụ đề"
      },
      payment: {
        title: "Lấy phụ đề video đầy đủ",
        description: "Xử lý video đầy đủ và tải xuống phụ đề chất lượng cao",
        features: {
          fullVideo: "Xử lý video đầy đủ",
          highQuality: "Phụ đề chất lượng cao",
          multipleFormats: "Nhiều định dạng",
          translationIncluded: "Bao gồm dịch thuật"
        },
        paypal: "PayPal",
        paypalDesc: "Thanh toán bằng tài khoản PayPal",
        creditCard: "Thẻ tín dụng",
        via2Checkout: "Qua 2Checkout",
        security: {
          secure: "Thanh toán an toàn",
          ssl: "Mã hóa SSL",
          instant: "Tải xuống ngay lập tức",
          afterProcessing: "Sau khi xử lý",
          autoDelete: "Tự động xóa",
          filesRemoved: "File sẽ bị xóa sau 1 giờ"
        }
      },
      queue: {
        title: "Xử lý video đầy đủ",
        queuePosition: "Vị trí trong hàng đợi: #{position}",
        estimatedWait: "Thời gian chờ ước tính: {time}",
        batchProcessing: "Xử lý hàng loạt ({current}/{total} đoạn)",
        currentSegment: "Đoạn hiện tại: {timeRange}",
        stayOnPage: "Giữ nguyên trang này",
        notificationDesc: "Bạn sẽ được thông báo khi xử lý hoàn tất và file sẵn sàng tải xuống."
      },
      download: {
        title: "Xử lý hoàn tất!",
        description: "Phụ đề của bạn đã sẵn sàng tải xuống",
        subtitleFiles: "File phụ đề",
        videoWithSubtitles: "Video với phụ đề",
        downloadAll: "Tải xuống tất cả file",
        autoDeleteWarning: "File sẽ tự động bị xóa trong {time} phút"
      },
      languages: {
        english: "Tiếng Anh",
        german: "Tiếng Đức",
        french: "Tiếng Pháp",
        spanish: "Tiếng Tây Ban Nha",
        italian: "Tiếng Ý"
      },
      footer: {
        features: "Tính năng",
        speechRecognition: "Nhận dạng giọng nói",
        multiLanguage: "Hỗ trợ đa ngôn ngữ",
        autoTranslation: "Dịch thuật tự động",
        multipleFormats: "Nhiều định dạng",
        support: "Hỗ trợ",
        helpCenter: "Trung tâm trợ giúp",
        apiDocs: "Tài liệu API",
        contact: "Liên hệ chúng tôi",
        status: "Trang trạng thái",
        legal: "Pháp lý",
        privacy: "Chính sách bảo mật",
        terms: "Điều khoản dịch vụ",
        cookies: "Chính sách cookie",
        copyright: "© 2024 SubGen. Bảo lưu mọi quyền."
      },
      home: {
        hero: {
          title: "Công cụ tạo phụ đề từ video",
          subtitle: "Tạo phụ đề SRT từ video",
          description: "Chuyển đổi video sang định dạng phụ đề với công nghệ AI. Tạo file SRT chính xác, hỗ trợ đa ngôn ngữ và xem trước miễn phí. Công cụ tạo phụ đề video trực tuyến tốt nhất."
        },
        features: {
          title: "Tính năng chuyển đổi video sang phụ đề tốt nhất"
        },
        pricing: {
          title: "Giá cả đơn giản, minh bạch",
          price: "$0,10",
          perMinute: "mỗi phút",
          description: "Không có đăng ký. Không có phí ẩn. Chỉ trả tiền cho những gì bạn xử lý.",
          freePreview: "Xem trước miễn phí (20-30 giây)",
          multipleFormats: "Nhiều định dạng (SRT, VTT, ASS)",
          autoTranslation: "Dịch thuật tự động"
        },
        testimonials: {
          title: "Được tin tưởng bởi các nhà sáng tạo nội dung toàn cầu"
        },
        technology: {
          title: "Công nghệ AI tiên tiến chuyển đổi video sang phụ đề",
          description: "Công cụ tạo phụ đề video của chúng tôi sử dụng OpenAI Whisper AI để nhận dạng chính xác và Google Translate để tạo phụ đề đa ngôn ngữ. Công cụ chuyển đổi video sang SRT tiên tiến nhất hiện có."
        }
      }
    }
  },
  es: {
    translation: {
      brand: "SubGen",
      tagline: "Generación de subtítulos con IA",
      nav: {
        features: "Características",
        pricing: "Precios",
        about: "Acerca de",
        support: "Soporte"
      },
      upload: {
        title: "Sube tu video o audio",
        description: "Formatos soportados: MP4, AVI, MOV, MP3, WAV, M4A (Máx 2GB)",
        dragDrop: "Arrastra y suelta tu archivo aquí, o",
        browse: "navegar",
        urlPlaceholder: "https://youtube.com/watch?v=...",
        load: "Cargar",
        jobInProgress: "Ya tienes un trabajo de procesamiento de video en progreso. Por favor espera a que se complete antes de subir otro video.",
        jobInProgressDetails: "Trabajo en progreso: {type} ({status})"
      },
      processing: {
        title: "Estado del procesamiento",
        fileAnalysis: "Análisis del archivo completado",
        audioExtraction: "Extracción de audio",
        speechRecognition: "Reconocimiento de voz (Vista previa)",
        translation: "Traducción (Opcional)",
        complete: "✓ Completado",
        processing: "Procesando...",
        pending: "Pendiente",
        sourceLanguage: "Idioma de origen",
        targetLanguage: "Traducir a",
        autoDetect: "Detección automática",
        noTranslation: "Sin traducción"
      },
      preview: {
        title: "Vista previa (Primeros {duration} segundos)",
        generatedSubtitles: "Subtítulos generados",
        edit: "Editar",
        download: "Descargar SRT",
        subtitleFormat: "Formato de subtítulos"
      },
      payment: {
        title: "Obtener subtítulos completos del video",
        description: "Procesar el video completo y descargar subtítulos de alta calidad",
        features: {
          fullVideo: "Procesamiento completo del video",
          highQuality: "Subtítulos de alta calidad",
          multipleFormats: "Múltiples formatos",
          translationIncluded: "Traducción incluida"
        },
        paypal: "PayPal",
        paypalDesc: "Pagar con cuenta PayPal",
        creditCard: "Tarjeta de crédito",
        via2Checkout: "Via 2Checkout",
        security: {
          secure: "Pago seguro",
          ssl: "Encriptado SSL",
          instant: "Descarga instantánea",
          afterProcessing: "Después del procesamiento",
          autoDelete: "Auto-eliminación",
          filesRemoved: "Archivos eliminados después de 1 hora"
        }
      },
      queue: {
        title: "Procesamiento completo del video",
        queuePosition: "Posición en cola: #{position}",
        estimatedWait: "Tiempo de espera estimado: {time}",
        batchProcessing: "Procesamiento por lotes ({current}/{total} segmentos)",
        currentSegment: "Segmento actual: {timeRange}",
        stayOnPage: "Mantente en esta página",
        notificationDesc: "Serás notificado cuando el procesamiento esté completo y los archivos estén listos para descargar."
      },
      download: {
        title: "¡Procesamiento completado!",
        description: "Tus subtítulos están listos para descargar",
        subtitleFiles: "Archivos de subtítulos",
        videoWithSubtitles: "Video con subtítulos",
        downloadAll: "Descargar todos los archivos",
        autoDeleteWarning: "Los archivos se eliminarán automáticamente en {time} minutos"
      },
      languages: {
        english: "Inglés",
        german: "Alemán",
        french: "Francés",
        spanish: "Español",
        italian: "Italiano"
      },
      footer: {
        features: "Características",
        speechRecognition: "Reconocimiento de voz",
        multiLanguage: "Soporte multi-idioma",
        autoTranslation: "Traducción automática",
        multipleFormats: "Múltiples formatos",
        support: "Soporte",
        helpCenter: "Centro de ayuda",
        apiDocs: "Documentación API",
        contact: "Contáctanos",
        status: "Página de estado",
        legal: "Legal",
        privacy: "Política de privacidad",
        terms: "Términos de servicio",
        cookies: "Política de cookies",
        copyright: "© 2024 SubGen. Todos los derechos reservados."
      },
      home: {
        hero: {
          title: "Generador de subtítulos de video",
          subtitle: "Crear subtítulos SRT desde video",
          description: "Convierte video a formato de subtítulos con transcripción impulsada por IA. Genera archivos SRT precisos, admite múltiples idiomas y obtén una vista previa gratuita. El mejor generador de subtítulos de video en línea."
        },
        features: {
          title: "Mejores características del convertidor de video a subtítulos"
        },
        pricing: {
          title: "Precios simples y transparentes",
          price: "$0.10",
          perMinute: "por minuto",
          description: "Sin suscripciones. Sin tarifas ocultas. Paga solo por lo que procesas.",
          freePreview: "Vista previa gratuita (20-30 segundos)",
          multipleFormats: "Múltiples formatos (SRT, VTT, ASS)",
          autoTranslation: "Traducción automática"
        },
        testimonials: {
          title: "Confiado por creadores de contenido en todo el mundo"
        },
        technology: {
          title: "Tecnología avanzada de IA para convertir video a subtítulos",
          description: "Nuestro generador de subtítulos de video usa OpenAI Whisper AI para transcripción precisa y Google Translate para generación de subtítulos multilingüe. El convertidor de video a SRT más avanzado disponible."
        }
      }
    }
  }
};

// Function to detect browser language
function detectBrowserLanguage(): string {
  // Get browser language
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  
  // Extract language code (e.g., 'en-US' -> 'en')
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Check if we support this language
  const supportedLanguages = Object.keys(resources);
  if (supportedLanguages.includes(langCode)) {
    return langCode;
  }
  
  // Fallback to English if language not supported
  return 'en';
}

// Get stored language preference or detect from browser
function getInitialLanguage(): string {
  // Check localStorage first
  const storedLang = localStorage.getItem('i18n_language');
  if (storedLang && Object.keys(resources).includes(storedLang)) {
    return storedLang;
  }
  
  // Auto-detect from browser
  return detectBrowserLanguage();
}

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'htmlTag'],
      caches: ['localStorage'],
      lookupFromPathIndex: 0,
      checkWhitelist: true
    }
  });

// Listen for language changes and save to localStorage
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('i18n_language', lng);
});

export default i18n;
